import { useSession } from '../lib/session.js';
import { describeAgent, OUTCOME_LABELS, OUTCOME_PILL, useLoginHistory } from '../lib/security.js';
import { formatDateTime } from '../lib/format.js';

/* ============================================================================
   /security — who has tried to get in.
   ----------------------------------------------------------------------------
   Readable by any admin, but not the same page for everyone: the owner sees
   every account and every address that was tried, anybody else sees their own.
   The API decides that from the session and says which it chose; this screen
   reports what it was given. Sending `?adminId=` from here would not widen it.

   The thing worth looking at is a run of "No such account" rows against
   addresses that were never registered — support@, accounts@, info@. That is
   somebody working through a list, and it is invisible from inside any single
   account's own history. It is why the count has its own tile.

   Cards rather than a table: this dashboard has no table styles, and the owner
   reads it on a phone. Same vocabulary as /invoices.
   ========================================================================== */

export function SecurityPage() {
  const { user } = useSession();
  const history = useLoginHistory();

  const attempts = history.data?.attempts ?? [];
  const scope = history.data?.scope;

  /* Counted over the rows that are already here rather than asked of the API.
     A second endpoint returning a number that could disagree with the list
     underneath it is worse than no number. */
  const failures = attempts.filter((a) => a.outcome !== 'success').length;
  const strangers = attempts.filter((a) => a.outcome === 'no_account').length;

  return (
    <section>
      <header className="page-head">
        <h1>Security</h1>
        <p className="muted">
          {scope === 'all'
            ? 'Every attempt to sign in to this dashboard, newest first.'
            : 'Your own sign-in attempts, newest first.'}{' '}
          Kept for 90 days, then deleted.
        </p>
      </header>

      {history.isPending ? <p className="muted">Loading…</p> : null}

      {history.isError ? (
        <p className="field__error" role="alert">
          Could not load the sign-in history.
        </p>
      ) : null}

      {history.isSuccess ? (
        <>
          <div className="row" style={{ gap: 20, marginBottom: 16, flexWrap: 'wrap' }}>
            <div>
              <strong>{attempts.length}</strong> <span className="muted">attempts shown</span>
            </div>
            <div>
              <strong>{failures}</strong> <span className="muted">did not get in</span>
            </div>
            {scope === 'all' ? (
              <div>
                <strong>{strangers}</strong>{' '}
                <span className="muted">against addresses with no account</span>
              </div>
            ) : null}
          </div>

          {attempts.length === 0 ? (
            <div className="card">
              <p style={{ margin: 0 }}>Nothing recorded yet.</p>
            </div>
          ) : (
            <div className="stack">
              {attempts.map((attempt) => (
                <article className="card" key={attempt.id}>
                  <div className="row" style={{ justifyContent: 'space-between', gap: 8 }}>
                    <div>
                      <strong>{formatDateTime(attempt.createdAt)}</strong>
                      <div className="lead-card__meta">
                        {scope === 'all' ? (
                          <>
                            {attempt.email}
                            {attempt.email === user?.email ? ' (you)' : ''} ·{' '}
                          </>
                        ) : null}
                        {/* The raw string in the title: the summary is a guess
                            from a self-reported header, and when it matters
                            you want what was actually sent. */}
                        <span title={attempt.userAgent ?? ''}>
                          {describeAgent(attempt.userAgent)}
                        </span>{' '}
                        · {attempt.ip ?? 'IP unknown'}
                      </div>
                    </div>
                    <span className={OUTCOME_PILL[attempt.outcome]} data-outcome={attempt.outcome}>
                      {OUTCOME_LABELS[attempt.outcome]}
                    </span>
                  </div>
                </article>
              ))}
            </div>
          )}

          <p className="muted" style={{ marginTop: 16 }}>
            Five wrong passwords within fifteen minutes locks an account for fifteen minutes. The
            sign-in screen does not say so — it answers a locked account exactly as it answers a
            typo, so that nobody can use it to find out which addresses are real.
          </p>
        </>
      ) : null}
    </section>
  );
}
